import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { GameState, Investigator } from '../api';
import { InvestigatorMarker } from './InvestigatorMarker';

const same = (left?: string, right?: string) => !!left && !!right && left.toLowerCase() === right.toLowerCase();

export const InvestigatorNavigation: React.FC<{
  investigators: Investigator[]; state?: GameState; firstName?: string; currentName: string;
  actingNames?: string[];
  open: boolean; onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>; onNavigate: (name: string) => void;
}> = ({ investigators, state, firstName, currentName, actingNames = [], open, onOpenChange, triggerRef, onNavigate }) => {
  const [optionIndex, setOptionIndex] = useState(0);
  const controlsRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const activeIndex = Math.min(optionIndex, investigators.length - 1);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!controlsRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open, onOpenChange]);
  useLayoutEffect(() => {
    if (!open) return;
    const placeMenu = () => {
      const menu = listRef.current;
      const trigger = triggerRef.current;
      if (menu && trigger) menu.style.left = `${Math.max(0, trigger.offsetLeft + trigger.offsetWidth - menu.offsetWidth)}px`;
    };
    placeMenu();
    listRef.current?.focus({ preventScroll: true });
    listRef.current?.children[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
    window.addEventListener('resize', placeMenu);
    return () => window.removeEventListener('resize', placeMenu);
  }, [open, activeIndex, triggerRef]);
  const showMenu = (index = investigators.findIndex(value => same(value.name, currentName))) => {
    setOptionIndex(Math.max(0, index));
    onOpenChange(true);
  };
  const badge = (value: Investigator, portrait = false) => same(value.name, firstName) &&
    <span id={portrait ? `${listId}-first` : undefined} className="game-inspector-first-badge" aria-label="First investigator this round" title="First investigator this round">1st</span>;
  return <div ref={controlsRef} className="game-inspector-controls" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) onOpenChange(false);
  }}>
    {actingNames.length > 0 && <span id={`${listId}-acting`} className="sr-only">Currently acting investigator</span>}
    <div className="game-inspector-portraits" role="group" aria-label="Investigator navigation">
      {investigators.map(value => <button key={value.name} type="button" className={`game-inspector-portrait${actingNames.some(name => same(name, value.name)) ? ' game-inspector-portrait-acting' : ''}`}
        aria-label={`Scroll to ${value.name}`} aria-describedby={[same(value.name, firstName) ? `${listId}-first` : '', actingNames.some(name => same(name, value.name)) ? `${listId}-acting` : ''].filter(Boolean).join(' ') || undefined}
        title={value.name} onClick={() => onNavigate(value.name)}>
        <span aria-hidden="true"><InvestigatorMarker investigator={{ name: value.name, delayed: false }} size={32} offsetByIndex={false} /></span>
        {badge(value, true)}
      </button>)}
    </div>
    {investigators.length > 1 && <button ref={triggerRef} type="button" className="game-inspector-selector" aria-label="Inspect investigator"
      aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
      onClick={() => open ? onOpenChange(false) : showMenu()} onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          showMenu(event.key === 'ArrowDown' ? 0 : investigators.length - 1);
        }
      }}><span aria-hidden="true">▾</span></button>}
    {open && <div ref={listRef} id={listId} className="game-inspector-options" role="listbox" tabIndex={0}
      aria-label="Inspect investigator" aria-activedescendant={`${listId}-${activeIndex}`} onKeyDown={event => {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) event.preventDefault();
        if (event.key === 'ArrowDown') setOptionIndex(index => (index + 1) % investigators.length);
        if (event.key === 'ArrowUp') setOptionIndex(index => (index + investigators.length - 1) % investigators.length);
        if (event.key === 'Home') setOptionIndex(0);
        if (event.key === 'End') setOptionIndex(investigators.length - 1);
        if (event.key === 'Enter' || event.key === ' ') onNavigate(investigators[activeIndex].name);
      }}>
      {investigators.map((value, index) => {
        const owner = state && state.expectedPlayersCount > 1 ? state.players?.find(player =>
          player.controlledInvestigators?.some(candidate => same(candidate, value.name))) : undefined;
        return <div key={value.name} id={`${listId}-${index}`} role="option" aria-selected={same(value.name, currentName)}
          className={index === activeIndex ? 'game-inspector-option-active' : undefined}
          onMouseMove={() => setOptionIndex(index)} onClick={() => onNavigate(value.name)}>
          <span>{value.name}{owner ? ` (${owner.name})` : ''}</span>{badge(value)}
        </div>;
      })}
    </div>}
  </div>;
};
